import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  InsightCategory,
  InsightsService as GeneratedInsightsService,
} from '../../../api';
import type {
  Action,
  Category,
  Confidence,
  Delta,
  FeedbackCategory,
  InsightDetail,
  InsightDetailResponse,
  InsightFeedback,
  InsightFeedbackRequest,
  InsightFilterParams,
  InsightListResponse,
  InsightSummaryCard,
  KeyFinding,
  RiskBreakdown,
  Tab,
} from './insights.models';
import type { InsightLevel, SelectedScope } from '../../shared/app-filters/app-filters.component';

@Injectable({ providedIn: 'root' })
export class InsightsService {
  private readonly api = inject(GeneratedInsightsService);

  getCategories(): Observable<InsightCategory[]> {
    return this.api.listInsightCategoriesInsightsCategoriesGet();
  }

  /**
   * Fetches the cards written at one level for the scope selected at it.
   *
   * The selection travels in the request body rather than the query string: a customer with a
   * few hundred sites would otherwise overflow the request line.
   *
   * @param level The level the user is looking at.
   * @param domain The insight domain, for example 'safety'.
   * @param scope The source identifiers selected at that level.
   * @param filters The filters shared by every level.
   * @returns The cards for that selection.
   */
  getInsightsForLevel(
    level: InsightLevel,
    domain: string,
    scope: SelectedScope,
    filters?: InsightFilterParams,
  ): Observable<InsightListResponse> {
    const shared = {
      dateOption: filters?.dateOption,
      dateFrom: filters?.dateFrom,
      dateTo: filters?.dateTo,
      riskLevels: filters?.riskLevels,
      platforms: filters?.platforms,
      categories: filters?.categories,
      trend: filters?.trend,
      sortBy: filters?.sortBy,
      sortAscending: filters?.sortAscending,
    };

    const call = level === 'enterprise'
      ? this.api.listEnterpriseInsightsInsightsEnterpriseDomainPost(domain, {
        ...shared,
        companyId: scope.companyId ?? '',
      })
      : level === 'organisation'
        ? this.api.listOrganisationInsightsInsightsOrganisationDomainPost(domain, {
          ...shared,
          organisationIds: scope.organisationIds,
        })
        : this.api.listSiteInsightsInsightsSiteDomainPost(domain, {
          ...shared,
          siteIds: scope.siteIds,
          facilityIds: scope.facilityIds,
        });

    return call.pipe(map((response) => this.mapListResponse(response)));
  }

  getInsightDetail(id: string): Observable<InsightDetailResponse> {
    return this.api
      .getInsightDetailInsightsDetailInsightIdGet(id)
      .pipe(map((response) => this.mapDetailResponse(response)));
  }

  /**
   * Returns the raw upsert response; the calling feature component owns the insights signal and
   * toast, since this service holds no shared state of its own.
   */
  submitFeedback(occurrenceId: string, request: InsightFeedbackRequest): Observable<InsightFeedback> {
    return this.api
      .upsertInsightFeedbackInsightsOccurrenceIdFeedbackPut(occurrenceId, request)
      .pipe(map((response) => this.mapFeedback(response)));
  }

  private mapListResponse(response: import('../../../api').InsightListResponse): InsightListResponse {
    return {
      items: response.items.map((item) => this.mapSummaryCard(item)),
      total: response.total,
    };
  }

  private mapDetailResponse(response: import('../../../api').InsightDetailResponse): InsightDetailResponse {
    return {
      summary: this.mapSummaryCard(response.summary),
      detail: this.mapInsightDetail(response.detail),
    };
  }

  private mapSummaryCard(
    card: import('../../../api').InsightSummaryCard &
      Pick<InsightSummaryCard, 'scopeEntityType' | 'scopeEntityId' | 'scopeDisplayName'>,
  ): InsightSummaryCard {
    return {
      id: card.id,
      title: card.title,
      platform: card.platform,
      scopeEntityType: card.scopeEntityType ?? null,
      scopeEntityId: card.scopeEntityId ?? null,
      scopeDisplayName: card.scopeDisplayName ?? null,
      category: this.mapCategory(card.category),
      confidence: this.mapConfidence(card.confidence),
      dateUpdated: card.dateUpdated,
      description: card.description,
      riskBreakdown: card.riskBreakdown ? this.mapRiskBreakdown(card.riskBreakdown) : undefined,
      keyFindings: (card.keyFindings ?? []).map((finding) => this.mapKeyFinding(finding)),
      actions: (card.actions ?? []).map((action) => this.mapAction(action)),
      feedback: card.feedback ? this.mapFeedback(card.feedback) : undefined,
    };
  }

  private mapInsightDetail(
    detail: import('../../../api').InsightDetail & Pick<InsightDetail, 'windowStart' | 'windowEnd'>,
  ): InsightDetail {
    return {
      id: detail.id,
      title: detail.title,
      description: detail.description,
      severity: detail.severity,
      timeWindow: detail.timeWindow,
      windowStart: detail.windowStart,
      windowEnd: detail.windowEnd,
      confidence: this.mapConfidence(detail.confidence),
      trend: detail.trend ? this.mapDelta(detail.trend) : undefined,
      actions: (detail.actions ?? []).map((action) => this.mapAction(action)),
      tabs: (detail.tabs ?? []).map((tab) => this.mapTab(tab)),
      feedback: detail.feedback ? this.mapFeedback(detail.feedback) : undefined,
    };
  }

  private mapCategory(category: import('../../../api').Category): Category {
    return {
      label: category.label,
      severity: category.severity,
    };
  }

  private mapConfidence(confidence: import('../../../api').Confidence): Confidence {
    return {
      value: confidence.value,
      unit: confidence.unit ?? 'percent',
      tooltip: confidence.tooltip ?? '',
    };
  }

  private mapRiskBreakdown(riskBreakdown: import('../../../api').RiskBreakdown): RiskBreakdown {
    return {
      critical: riskBreakdown.critical ?? 0,
      watch: riskBreakdown.watch ?? 0,
      monitoring: riskBreakdown.monitoring ?? 0,
      trend: riskBreakdown.trend ? this.mapDelta(riskBreakdown.trend) : undefined,
    };
  }

  private mapKeyFinding(finding: import('../../../api').KeyFinding): KeyFinding {
    return {
      label: finding.label,
      value: finding.value as number | string,
      metric: finding.metric ?? undefined,
    };
  }

  private mapDelta(delta: import('../../../api').Delta): Delta {
    return {
      value: delta.value,
      unit: delta.unit ?? 'percent',
      direction: delta.direction,
      sentiment: delta.sentiment,
      comparison: delta.comparison ?? undefined,
    };
  }

  private mapAction(action: import('../../../api').Action): Action {
    return {
      action: action.action,
      data: action.data,
    };
  }

  private mapTab(tab: import('../../../api').Tab): Tab {
    return {
      label: tab.label,
      content: (tab.content ?? []) as Tab['content'],
    };
  }

  private mapFeedback(feedback: import('../../../api').InsightFeedback): InsightFeedback {
    return {
      isPositive: feedback.isPositive,
      categories: (feedback.categories ?? []) as FeedbackCategory[],
      details: feedback.details ?? null,
      updatedAt: feedback.updatedAt,
    };
  }
}
